import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGiftContext } from './GiftContext';
import { showNavigationConfirmation, showNavigationConfirmationWithoutNavigation, hasUnsavedGiftProgress } from './navigationGuard';

export const useNavigationGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const giftContext = useGiftContext();
  
  // Track internal navigation flag
  let isInternalNavigation = false;

  // Intercept React Router navigation
  useEffect(() => {
    const handlePopState = async (event: PopStateEvent) => {
      // Check if user has unsaved progress
      if (hasUnsavedGiftProgress(giftContext?.currentGift, true, giftContext?.activeTab)) {
        // Prevent the navigation
        event.preventDefault();
        window.history.pushState(null, '', location.pathname);
        
        // Show confirmation modal
        const shouldNavigate = await showNavigationConfirmation({
          currentGift: giftContext?.currentGift,
          isInGiftCreation: true,
          saveGiftAsDraft: giftContext?.saveGiftAsDraft || (() => Promise.resolve()),
          navigate: navigate,
          targetPath: '/my-gifts',
          activeTab: giftContext?.activeTab
        });
        
        if (shouldNavigate) {
          // Allow navigation after user choice
          window.history.back();
        }
      }
    };

    // Handle internal gift navigation events
    const handleInternalNavigation = (event: CustomEvent) => {
      console.log('🔄 Internal gift navigation detected:', event.detail);
      isInternalNavigation = true;
      // Reset the flag after a longer delay to ensure proper handling
      setTimeout(() => {
        isInternalNavigation = false;
      }, 500);
    };

    // Handle navigation guard requests from parent components
    const handleNavigationGuardRequest = async (event: CustomEvent) => {
      console.log('🛡️ Navigation guard request received:', event.detail);
      
      const { targetPath, targetTab } = event.detail;
      
      try {
        // Skip navigation guard if this is internal navigation
        if (isInternalNavigation) {
          console.log('🔄 Internal navigation detected, allowing without confirmation');
          window.dispatchEvent(new CustomEvent('navigationGuardResponse', {
            detail: { shouldProceed: true }
          }));
          return;
        }
        
        // Check if we have unsaved progress
        if (hasUnsavedGiftProgress(giftContext?.currentGift, true, giftContext?.activeTab)) {
          console.log('🛡️ Has unsaved progress, showing confirmation...');
          
          // Call the navigation confirmation but DO NOT navigate here
          // The navigation should only happen in the parent component after user confirms
          const result = await showNavigationConfirmationWithoutNavigation({
            currentGift: giftContext?.currentGift,
            isInGiftCreation: true,
            saveGiftAsDraft: giftContext?.saveGiftAsDraft || (() => Promise.resolve()),
            targetPath: targetPath,
            activeTab: giftContext?.activeTab
          });
          
          console.log('🛡️ Navigation confirmation result:', result);
          
          // Always respond with the result, even if there was an error
          window.dispatchEvent(new CustomEvent('navigationGuardResponse', {
            detail: { shouldProceed: result }
          }));
        } else {
          console.log('🛡️ No unsaved progress, allowing navigation');
          // No unsaved progress, allow navigation
          window.dispatchEvent(new CustomEvent('navigationGuardResponse', {
            detail: { shouldProceed: true }
          }));
        }
      } catch (error) {
        console.error('🛡️ Error in navigation guard request handler:', error);
        // On error, block navigation for safety
        window.dispatchEvent(new CustomEvent('navigationGuardResponse', {
          detail: { shouldProceed: false }
        }));
      }
    };

    // Prevent browser back/forward during gift creation
    window.addEventListener('popstate', handlePopState);
    
    // Listen for internal gift navigation events
    window.addEventListener('internalGiftNavigation', handleInternalNavigation as EventListener);
    
    // Listen for navigation guard requests
    window.addEventListener('navigationGuardRequest', handleNavigationGuardRequest as EventListener);
    
    // Prevent page refresh/close during gift creation
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Don't show warning during payment processing or on delivery/payment step
      const isOnDeliveryStep = giftContext?.activeTab === '6';
      const isPaymentProcessing = location.pathname.includes('/gift-success') ||
                                  document.querySelector('.payment-processing') ||
                                  document.querySelector('[data-payment-processing]');
      // Check if gift completion is in progress (e.g., confirming skipped gift)
      const isGiftCompletionInProgress = sessionStorage.getItem('giftCompletionInProgress') === 'true';

      console.log('🔍 beforeunload check:', {
        hasProgress: hasUnsavedGiftProgress(giftContext?.currentGift, true),
        isOnDeliveryStep,
        isPaymentProcessing,
        isGiftCompletionInProgress,
        activeTab: giftContext?.activeTab,
        pathname: location.pathname
      });

      // Don't show warning if:
      // 1. On delivery/payment step (user is completing the gift)
      // 2. Payment is being processed
      // 3. Gift completion is in progress (e.g., confirming skipped gift)
      // 4. No unsaved progress
      if (isOnDeliveryStep || isPaymentProcessing || isGiftCompletionInProgress || !hasUnsavedGiftProgress(giftContext?.currentGift, true, giftContext?.activeTab)) {
        console.log('🔍 Allowing page unload without warning');
        // Clear the completion flag if it was set
        if (isGiftCompletionInProgress) {
          sessionStorage.removeItem('giftCompletionInProgress');
        }
        return;
      }

      console.log('🔍 Showing beforeunload warning');
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('internalGiftNavigation', handleInternalNavigation as EventListener);
      window.removeEventListener('navigationGuardRequest', handleNavigationGuardRequest as EventListener);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [giftContext?.currentGift, giftContext?.saveGiftAsDraft, navigate, location.pathname]);

  // Function to handle navigation with confirmation
  const handleNavigation = useCallback(async (targetPath: string) => {
    console.log('🛡️ handleNavigation called:', {
      targetPath,
      hasGiftData: !!giftContext?.currentGift,
      activeTab: giftContext?.activeTab,
      hasProgress: hasUnsavedGiftProgress(giftContext?.currentGift, true, giftContext?.activeTab)
    });
    
    if (hasUnsavedGiftProgress(giftContext?.currentGift, true, giftContext?.activeTab)) {
      console.log('🛡️ Showing navigation confirmation for:', targetPath);
      await showNavigationConfirmation({
        currentGift: giftContext?.currentGift,
        isInGiftCreation: true,
        saveGiftAsDraft: giftContext?.saveGiftAsDraft || (() => Promise.resolve()),
        navigate: navigate,
        targetPath: targetPath,
        activeTab: giftContext?.activeTab
      });
    } else {
      console.log('🛡️ No progress to save, allowing navigation to:', targetPath);
      // Dispatch approval event to prevent interception and allow direct navigation
      window.dispatchEvent(new CustomEvent('navigationApproved'));
      navigate(targetPath);
    }
  }, [giftContext?.currentGift, giftContext?.saveGiftAsDraft, navigate]);

  return {
    handleNavigation
  };
};